// Morrow — Connect an email account (IMAP/SMTP).
//
// The managed connect flow for any mail host Composio's Gmail/Outlook OAuth
// doesn't cover (Hostinger, Namecheap, Zoho, cPanel, …). Opened as its own page
// from the Connections tab: one centered card — type your email + app-password,
// we autoconfig the host/port (editable under Advanced), hit Connect, and the
// server VERIFIES against the real IMAP+SMTP servers before storing the password
// encrypted. We read/send on demand; we don't sync or store your mail.
//
// Backed by /api/morrow/email/{providers,autoconfig,connect,accounts,disconnect}.

import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  Loader2Icon,
  MailIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { resolveComposioUserId } from "../../composioConnect";
import { cn } from "../../lib/utils";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SettingsPageContainer } from "./settingsLayout";

type Security = "tls" | "starttls";
type ServerSettings = { host: string; port: number; security: Security };
type Provider = {
  id: string;
  label: string;
  imap: ServerSettings;
  smtp: ServerSettings;
  authNote?: string;
};
type Account = {
  id: string;
  email: string;
  providerId: string | null;
  imapHost: string;
  smtpHost: string;
  status: string;
  createdAt: string;
};

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: { accept: "application/json" } });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}
async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    return (await res.json().catch(() => null)) as T | null;
  } catch {
    return null;
  }
}

const LabeledInput = ({
  label,
  ...props
}: { label: string } & React.ComponentProps<typeof Input>) => (
  <label className="flex flex-1 flex-col gap-1">
    <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
    <Input {...props} className={cn("h-9", props.className)} />
  </label>
);

export function EmailConnectPage() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isLoaded && isSignedIn ? clerkUserId : null),
    [isLoaded, isSignedIn, clerkUserId],
  );

  const [providers, setProviders] = useState<Provider[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [imapSecurity, setImapSecurity] = useState<Security>("tls");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpSecurity, setSmtpSecurity] = useState<Security>("tls");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshAccounts = useCallback(() => {
    if (!userId) return;
    void getJson<{ accounts?: Account[] }>(
      `/api/morrow/email/accounts?userId=${encodeURIComponent(userId)}`,
    ).then((d) => setAccounts(Array.isArray(d?.accounts) ? d!.accounts : []));
  }, [userId]);

  useEffect(() => {
    void getJson<{ providers?: Provider[] }>("/api/morrow/email/providers").then((d) =>
      setProviders(Array.isArray(d?.providers) ? d!.providers : []),
    );
  }, []);
  useEffect(() => refreshAccounts(), [refreshAccounts]);

  const applyServers = useCallback(
    (imap: ServerSettings, smtp: ServerSettings, pid: string | null, authNote?: string | null) => {
      setImapHost(imap.host);
      setImapPort(imap.port);
      setImapSecurity(imap.security);
      setSmtpHost(smtp.host);
      setSmtpPort(smtp.port);
      setSmtpSecurity(smtp.security);
      setProviderId(pid);
      setNote(authNote ?? null);
    },
    [],
  );

  const pickProvider = useCallback(
    (p: Provider) => applyServers(p.imap, p.smtp, p.id, p.authNote ?? null),
    [applyServers],
  );

  // On email blur, ask the server to autoconfig host/port from the domain.
  const autoconfig = useCallback(async () => {
    const value = email.trim();
    if (!value.includes("@") || imapHost) return; // don't clobber a manual/picked host
    const res = await postJson<{
      imap?: ServerSettings;
      smtp?: ServerSettings;
      providerId?: string | null;
      authNote?: string | null;
    }>("/api/morrow/email/autoconfig", { email: value });
    if (res?.imap && res?.smtp)
      applyServers(res.imap, res.smtp, res.providerId ?? null, res.authNote);
  }, [applyServers, email, imapHost]);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    const res = await postJson<{ ok?: boolean; id?: string; error?: string }>(
      "/api/morrow/email/connect",
      {
        userId,
        email: email.trim(),
        password,
        providerId,
        imapHost,
        imapPort,
        imapSecurity,
        smtpHost,
        smtpPort,
        smtpSecurity,
      },
    );
    setBusy(false);
    if (res?.ok) {
      setEmail("");
      setPassword("");
      setImapHost("");
      setSmtpHost("");
      setProviderId(null);
      setNote(null);
      refreshAccounts();
    } else {
      setError(res?.error ?? "Couldn't connect that account.");
    }
  }, [
    email,
    imapHost,
    imapPort,
    imapSecurity,
    password,
    providerId,
    refreshAccounts,
    smtpHost,
    smtpPort,
    smtpSecurity,
    userId,
  ]);

  const disconnect = useCallback(
    async (id: string) => {
      await postJson("/api/morrow/email/disconnect", { userId, id });
      refreshAccounts();
    },
    [refreshAccounts, userId],
  );

  const canConnect =
    email.trim().includes("@") && password.length > 0 && imapHost && smtpHost && !busy;

  return (
    <SettingsPageContainer>
      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6">
        <button
          type="button"
          onClick={() => void navigate({ to: "/settings/connections" })}
          className="flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" /> Connections
        </button>

        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl border border-border bg-muted">
            <MailIcon className="size-6 text-foreground" />
          </span>
          <h1 className="text-lg font-semibold text-foreground">Connect an email account</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Any IMAP/SMTP mailbox — Hostinger, Namecheap, Zoho, cPanel, your own host. Morrow reads
            and sends on demand; it never stores your mail.
          </p>
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
          {providers.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">
                Quick pick a provider
              </span>
              <div className="flex flex-wrap gap-1.5">
                {providers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickProvider(p)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition-colors",
                      providerId === p.id
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-muted text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-3">
            <LabeledInput
              label="Email address"
              type="email"
              placeholder="you@yourcompany.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => void autoconfig()}
              autoComplete="email"
            />
            <LabeledInput
              label="Password (or app-password)"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          {note ? (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-600 dark:text-amber-400">
              {note}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="flex items-center gap-1 self-start text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronDownIcon
              className={cn("size-3.5 transition-transform", advanced && "rotate-180")}
            />
            Advanced server settings
          </button>

          {advanced ? (
            <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="flex gap-2">
                <LabeledInput
                  label="IMAP host"
                  value={imapHost}
                  onChange={(e) => setImapHost(e.target.value)}
                  placeholder="imap.host.com"
                />
                <LabeledInput
                  label="Port"
                  type="number"
                  className="max-w-24"
                  value={imapPort}
                  onChange={(e) => setImapPort(Number(e.target.value) || 993)}
                />
              </div>
              <div className="flex gap-2">
                <LabeledInput
                  label="SMTP host"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.host.com"
                />
                <LabeledInput
                  label="Port"
                  type="number"
                  className="max-w-24"
                  value={smtpPort}
                  onChange={(e) => {
                    const p = Number(e.target.value) || 465;
                    setSmtpPort(p);
                    setSmtpSecurity(p === 587 ? "starttls" : "tls");
                  }}
                />
              </div>
            </div>
          ) : null}

          {error ? <p className="text-xs text-destructive">{error}</p> : null}

          <Button onClick={() => void connect()} disabled={!canConnect} className="w-full">
            {busy ? (
              <>
                <Loader2Icon className="size-4 animate-spin" /> Verifying &amp; connecting…
              </>
            ) : (
              <>
                <ShieldCheckIcon className="size-4" /> Connect
              </>
            )}
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">
            We sign in to verify it works, then store your password encrypted. It's never shown
            again.
          </p>
        </div>

        {accounts.length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted-foreground">Connected accounts</span>
            {accounts.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <CheckIcon className="size-4 text-emerald-500" />
                  <div>
                    <div className="text-xs font-medium text-foreground">{a.email}</div>
                    <div className="text-[11px] text-muted-foreground">{a.imapHost}</div>
                  </div>
                </div>
                <Button size="xs" variant="ghost" onClick={() => void disconnect(a.id)}>
                  <Trash2Icon className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SettingsPageContainer>
  );
}
